import {
  Box,
  Stack,
  FormControl,
  InputAdornment,
  TextField,
  Button,
  Typography,
  Tooltip,
  SelectChangeEvent,
} from '@mui/material';
import i18n from '../../libs/language';
import { stylesInput } from '@old/styles/generic-styles';
import { useGlobalStore } from '@old/global-store/use-store';
import { useService } from '@old/hooks/use-service';
import SearchIcon from '@old/assets/svg/search.svg';
import { useRef } from 'react';
import DeleteIcon from '@old/assets/svg/core/trash-red.svg';
import { TabletStateStock } from '@old/features/report-state-stock/tablet-state-stock';
import { useDebounce } from '@old/hooks/core';
import { useEffectOnUpdate } from '@old/hooks/core';
import { listCountrysideService } from '@old/services/domain/base/countryside/countryside-list';
import { listLotsService } from '@old/services/domain/base/lot/lot-list';
import { stockStatusListService } from '@old/services/domain/page/report/status-stock/report-status-stock.info';
import { listWebSilobagsService } from '@old/services/domain/base/silobag/base/silobag-web';
import { Selector } from '@old/ui/selector/selector';
import { GenerateReportStateStockExcelData } from '@old/utils/export-files';
import downloadPdfIcon from '@old/assets/svg/downloadIcon.svg';

export function StateStockPage() {
  const { organizationId, filtersReportStateStock, setFiltersReportStateStock } = useGlobalStore();
  const inputRef = useRef<HTMLInputElement>(null);

  const { trigger: triggerListWebSilobags, summary: listWebSilobagsCount } = useService(
    listWebSilobagsService,
    {
      fetchOnMount: [organizationId],
    }
  );

  //StateStock Service
  const {
    trigger: getStateStock,
    summary: stateStockData,
    isLoading: isLoadingStateStock,
  } = useService(stockStatusListService, {
    fetchOnMount: true,
  });
  //Countrysides Service
  const queryCountrysideList = { organization_id: organizationId };
  const {
    //trigger: getCountrysides,
    summary: countrysides,
    isLoading: isLoadingCountryside,
    trigger: getCountrysides,
  } = useService(listCountrysideService, { fetchOnMount: [{ query: queryCountrysideList }] });

  //Lots Service
  const { trigger: getLots, summary: lots, isLoading: isLoadingLots } = useService(listLotsService, {});

  useDebounce(() => getStateStock(), 500, [filtersReportStateStock.name]);

  const handlerCountrysideID = (e: SelectChangeEvent) => {
    const selectedValue = e.target.value;
    setFiltersReportStateStock({ ...filtersReportStateStock, countrysideId: selectedValue, lotId: '' });
  };

  const handlerLotID = (e: SelectChangeEvent) => {
    setFiltersReportStateStock({ ...filtersReportStateStock, lotId: e.target.value });
  };

  const removeFilter = () => {
    setFiltersReportStateStock({
      name: '',
      countrysideId: '',
      lotId: '',
      paginationModel: { page: 0, pageSize: 50 },
    });
    getStateStock();
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  useEffectOnUpdate(() => {
    getStateStock();
  }, [filtersReportStateStock.countrysideId, filtersReportStateStock.lotId, organizationId]);

  useEffectOnUpdate(() => {
    getCountrysides({ query: queryCountrysideList });
    triggerListWebSilobags(organizationId);
  }, [organizationId]);

  useEffectOnUpdate(() => {
    if (filtersReportStateStock.countrysideId) {
      getLots({ query: { countryside_id: Number(filtersReportStateStock.countrysideId) } });
    }
  }, [filtersReportStateStock.countrysideId]);

  const deleteCountrysideFilter = () => {
    setFiltersReportStateStock({ ...filtersReportStateStock, countrysideId: '', lotId: '' });
  };

  const deleteLotFilter = () => {
    setFiltersReportStateStock({ ...filtersReportStateStock, lotId: '' });
  };

  return (
    <Box sx={{ width: '95%', margin: 'auto' }}>
      <Stack direction="row" alignItems="center" spacing={2} py={2}>
        <Box component="h1" fontWeight={500} style={{ color: '#49454F' }} flex={1}>
          {i18n.t('app.state_stock_page.report_state_stock')}
        </Box>
        <Button
          startIcon={<img src={downloadPdfIcon} alt="download" />}
          variant="outlined"
          sx={{
            borderRadius: 100,
            margin: '10px',
            padding: '10px 30px',
            fontSize: 12,
            fontWeight: 500,
            textTransform: 'uppercase',
            whiteSpace: 'nowrap',
            minWidth: '150px',
          }}
          onClick={() => GenerateReportStateStockExcelData(stateStockData?.rows ?? [], countrysides)}
        >
          {i18n.t('app.silobag_list.export_all')}
        </Button>
      </Stack>
      <Box component="section" position={'relative'}>
        <Stack direction={'row'} gap={1}>
          <FormControl style={{ width: '50%', fontSize: 14, marginRight: '10px' }}>
            <TextField
              size="medium"
              variant="outlined"
              placeholder={i18n.t('app.state_stock_page.search_by_name_silo_countryside')}
              style={stylesInput}
              value={filtersReportStateStock.name}
              onChange={(e) => {
                setFiltersReportStateStock({ ...filtersReportStateStock, name: e.target.value });
              }}
              inputRef={inputRef}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <img src={SearchIcon} alt="search-icon" />
                    </InputAdornment>
                  ),
                  inputProps: { style: { fontSize: 13, height: 22 } },
                },
              }}
            />
          </FormControl>
          <FormControl
            variant="outlined"
            size="medium"
            style={{ width: '45%', fontSize: 14, marginRight: '10px' }}
          >
            <Selector
              label={i18n.t('app.countrysides')}
              id="countryside_id"
              value={filtersReportStateStock.countrysideId}
              onChange={(e) => handlerCountrysideID(e as SelectChangeEvent)}
              options={countrysides}
              selectedList={filtersReportStateStock.countrysideId}
              renderValue={() =>
                countrysides
                  ?.filter(
                    (countryside) => filtersReportStateStock.countrysideId == countryside?.id.toString()
                  )
                  .map((matchedCountryside) => matchedCountryside?.name)
              }
              deleteFilter={deleteCountrysideFilter}
              disabled={isLoadingCountryside}
            />
          </FormControl>
          <FormControl
            variant="outlined"
            size="medium"
            style={{ width: '45%', fontSize: 14, marginRight: '10px' }}
          >
            <Selector
              label={i18n.t('app.lots')}
              id="lot_id"
              value={filtersReportStateStock.lotId}
              onChange={(e) => handlerLotID(e as SelectChangeEvent)}
              options={lots}
              selectedList={filtersReportStateStock.lotId}
              renderValue={() =>
                lots
                  ?.filter((lot) => filtersReportStateStock.lotId == lot?.id.toString())
                  .map((matchedLot) => matchedLot?.name)
              }
              deleteFilter={deleteLotFilter}
              disabled={!filtersReportStateStock.countrysideId || isLoadingLots}
            />
          </FormControl>
          <Button
            sx={{
              fontSize: 12,
              fontWeight: 500,
              paddingInline: 3,
              textTransform: 'uppercase',
              visibility:
                filtersReportStateStock.countrysideId ||
                filtersReportStateStock.lotId ||
                filtersReportStateStock.name
                  ? ''
                  : 'hidden',
              color: '#FF0000',
              '&:hover': { background: '#FF000022' },
            }}
            onClick={removeFilter}
            startIcon={<img src={DeleteIcon} alt="delete-icon" />}
          >
            {i18n.t('app.clear_filters')}
          </Button>

          <Box width={'100%'} display={'flex'} justifyContent={'end'}>
            <Box component={'div'} width={'20%'} zIndex={'99'} position={'absolute'}>
              {listWebSilobagsCount && listWebSilobagsCount?.incomplete_silobags_count > 0 && (
                <Tooltip title={i18n.t('app.missing_tons')} placement="top-end">
                  <Typography color={'#7A7A7A'} textAlign={'end'} sx={{ cursor: 'default', marginRight: 2 }}>
                    {i18n.t('app.missing_tons_title')}
                  </Typography>
                </Tooltip>
              )}
            </Box>
          </Box>
        </Stack>
      </Box>
      <Box>
        {/* Componente de la tabla de la vista */}
        <TabletStateStock
          data={stateStockData?.rows ?? []}
          count={stateStockData?.count ?? 0}
          isLoading={isLoadingStateStock}
          //paginationModel={filtersReportStateStock?.paginationModel}
          setPaginationModel={(fn) => {
            const paginationModel = fn(filtersReportStateStock.paginationModel);
            setFiltersReportStateStock({ ...filtersReportStateStock, paginationModel });
          }}
        />
      </Box>
    </Box>
  );
}
